import { CalculationRule } from '../../../types/form';

/**
 * Evaluates a calculation formula with form values.
 * Supports basic math AND ternary-style conditionals:
 *   (condition) ? valueIfTrue : valueIfFalse
 *
 * Supported operators in conditions: ==, !=, >, <, >=, <=
 * Supported in formulas: +, -, *, /, (, )
 *
 * Examples:
 *   "impacted_parcels * 8"
 *   "(research_is_government_owned == 1) ? 11 : 8"
 *   "(research_is_government_owned == 1) ? 11 : 8) * impacted_parcels"
 */
export const evaluateCalculation = (
  calculation: CalculationRule | undefined,
  formValues: Record<string, any>
): number | null => {
  if (!calculation || !calculation.formula) return null;

  try {
    let formula = calculation.formula;

    // Replace field names with their numeric values
    for (const fieldName of calculation.fields) {
      const raw = formValues[fieldName];
      // Booleans / "true"/"false" strings → 1 / 0
      let value: number;
      if (raw === true || raw === 'true') value = 1;
      else if (raw === false || raw === 'false') value = 0;
      else value = Number(raw) || 0;

      formula = formula.replace(new RegExp(`\\b${fieldName}\\b`, 'g'), String(value));
    }

    const result = evaluateFormula(formula);
    if (result === null || isNaN(result)) return null;

    if (calculation.decimals !== undefined) {
      return Number(result.toFixed(calculation.decimals));
    }
    return result;
  } catch (error) {
    console.error('Calculation error:', error);
    return null;
  }
};

/**
 * Safely evaluates a math formula that may contain ternary expressions.
 * Pattern: (condition) ? trueVal : falseVal
 * Conditions may use: ==, !=, >=, <=, >, <
 */
const evaluateFormula = (formula: string): number | null => {
  try {
    formula = formula.replace(/\s/g, '');

    // Resolve all ternary expressions first (supports nesting via loop)
    let prev = '';
    while (prev !== formula) {
      prev = formula;
      // Match innermost ternary: (A op B)?C:D  where C and D are numbers/decimals
      formula = formula.replace(
        /\((-?[\d.]+)(==|!=|>=|<=|>|<)(-?[\d.]+)\)\?(-?[\d.]+):(-?[\d.]+)/g,
        (_match, a, op, b, t, f) => {
          const av = parseFloat(a);
          const bv = parseFloat(b);
          let cond = false;
          if (op === '==') cond = av === bv;
          else if (op === '!=') cond = av !== bv;
          else if (op === '>=') cond = av >= bv;
          else if (op === '<=') cond = av <= bv;
          else if (op === '>') cond = av > bv;
          else if (op === '<') cond = av < bv;
          return cond ? t : f;
        }
      );
    }

    // After resolving ternaries, only basic math should remain
    if (!/^[\d+\-*/.()]+$/.test(formula)) return null;

    // eslint-disable-next-line no-new-func
    const result = new Function(`return ${formula}`)();
    return typeof result === 'number' ? result : null;
  } catch {
    return null;
  }
};

export const getCalculationDependencies = (
  field: { calculation?: CalculationRule }
): string[] => {
  if (!field.calculation) return [];
  return field.calculation.fields || [];
};

export const validateCalculationFormula = (formula: string, fields: string[]): boolean => {
  try {
    let testFormula = formula;
    for (const field of fields) {
      testFormula = testFormula.replace(new RegExp(`\\b${field}\\b`, 'g'), '1');
    }
    return evaluateFormula(testFormula) !== null;
  } catch {
    return false;
  }
};
